// Peças compartilhadas das sub-abas do Portal do Cliente (Dev → Portal do Cliente).
// Tipos espelham apps/api/src/carmillion_api/routers/portal_analysis.py.

export type FeatureKey = "atendimento" | "analise_portal" | "insights" | "relatorios";
export type ByFeature = Record<FeatureKey, number>;

export const FEATURES: { key: FeatureKey; label: string; color: string }[] = [
  { key: "atendimento", label: "Atendimento IA", color: "var(--brand)" },
  { key: "analise_portal", label: "Análise do portal", color: "var(--info)" },
  { key: "insights", label: "Insights", color: "var(--violet)" },
  { key: "relatorios", label: "Relatórios", color: "var(--ink-muted)" },
];

export const input: React.CSSProperties = {
  padding: "8px 10px", fontSize: 13, color: "var(--ink)",
  background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 6,
  boxSizing: "border-box",
};

export const btn: React.CSSProperties = {
  padding: "7px 14px", fontSize: 13, borderRadius: 6, cursor: "pointer",
  border: "1px solid var(--line)", background: "var(--surface-card)", color: "var(--ink)",
};

export const btnSm: React.CSSProperties = { ...btn, fontSize: 12, padding: "5px 10px" };

export const primaryBtn: React.CSSProperties = {
  ...btn, background: "var(--brand)", color: "#fff", border: "1px solid var(--brand)",
};

export const label: React.CSSProperties = { fontSize: 12, color: "var(--ink-muted)" };

export const card: React.CSSProperties = {
  background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 12, padding: 16,
};

export const th: React.CSSProperties = {
  padding: "8px 10px", borderBottom: "1px solid var(--line)", fontWeight: 600,
  fontSize: 12, color: "var(--ink-muted)", textAlign: "left", whiteSpace: "nowrap",
};

export const td: React.CSSProperties = { padding: "8px 10px", borderBottom: "1px solid var(--line)" };

export const num: React.CSSProperties = { textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };

/** US$ com vírgula; centavo de centavo ganha 4 casas (custo de LLM é miúdo). */
export function fmtUsd(v: number | null | undefined): string {
  if (v == null) return "—";
  const digits = v !== 0 && Math.abs(v) < 0.01 ? 4 : 2;
  return `US$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

export function fmtInt(v: number): string {
  return v.toLocaleString("pt-BR");
}

/** "06/10 14:15" no horário de Brasília. */
export function fmtDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

/** "AAAA-MM-DD" → "dd/mm". */
export function fmtDay(day: string): string {
  return `${day.slice(8, 10)}/${day.slice(5, 7)}`;
}

export function todaySp(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
}

export function shiftDay(day: string, delta: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

/** Mensagem legível do erro do backend (detail string ou 1º item do 422 do Pydantic). */
export function errorText(e: unknown, fallback: string): string {
  const detail = (e as { payload?: { detail?: unknown } } | null)?.payload?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return String(detail[0].msg);
  return fallback;
}

export function Toggle({ on, onChange, disabled, title }: {
  on: boolean; onChange: (v: boolean) => void; disabled?: boolean; title?: string;
}) {
  return (
    <button
      type="button" role="switch" aria-checked={on} title={title} disabled={disabled}
      onClick={() => onChange(!on)}
      style={{ background: "none", border: "none", padding: 0, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1, display: "inline-flex" }}
    >
      <span style={{ width: 34, height: 20, borderRadius: 999, background: on ? "var(--brand)" : "var(--line-strong)", position: "relative", transition: "background .15s" }}>
        <span style={{ position: "absolute", top: 3, left: on ? 17 : 3, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left .15s" }} />
      </span>
    </button>
  );
}

export function Pill({ text, tone, title }: {
  text: string; tone: "ok" | "warn" | "danger" | "info" | "muted"; title?: string;
}) {
  const color = tone === "muted" ? "var(--ink-muted)" : `var(--${tone})`;
  const bg = tone === "muted" ? "var(--surface-hover)" : `var(--${tone}-soft)`;
  return (
    <span title={title} style={{ display: "inline-block", fontSize: 11, padding: "1px 8px", borderRadius: 99, color, background: bg, whiteSpace: "nowrap" }}>
      {text}
    </span>
  );
}
